import {
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
  formatDate,
  MbscResource
} from '@mobiscroll/react'
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import React, { FC, useCallback } from 'react'

import { customToggleButton } from '../../styles/customToggleButton'

export const useRenderCalendar = () => {
  const renderResource = useCallback((resource: MbscResource) => {
    if (resource && resource.name) {
      return (
        <Box>
          <Typography
            variant="body2"
            aria-label="resourceName"
            sx={{ wordWrap: 'break-word' }}>
            {resource!.name!.slice(0, 1).toString().toUpperCase() +
              resource!.name!.slice(1).toString()}
          </Typography>
        </Box>
      )
    }
  }, [])
  const renderDayHeader = useCallback((view: 'day' | 'week', args: any) => {
    const { events, date, selected } = args
    let carsToday = 0
    let hoursToday = 0
    if (events) {
      carsToday = events.length
      for (const event of events) {
        hoursToday =
          hoursToday + dayjs(event.end).diff(dayjs(event.start), 'hour', true)
      }
    }
    const color = {
      bgcolor: 'primary.contrastText',
      text: 'black',
      hoverBg: 'primary.light'
    }
    if (view === 'week') {
      color.hoverBg = 'primary.contrastText'
    }
    if (selected) {
      color.bgcolor = 'primary.main'
      color.text = 'primary.contrastText'
      color.hoverBg = 'primary.main'
    }

    if (!selected && date.getDay() === new Date().getDay()) {
      color.bgcolor = 'primary.contrastText'
      color.text = 'primary.main'
      color.hoverBg = 'primary.light'
    }
    return (
      <Stack justifyContent="center" alignItems="center" spacing={0.5}>
        <Stack
          direction={{ xs: 'column', xl: 'row' }}
          alignItems="center"
          sx={{ width: '100%' }}>
          <Box sx={{ width: 'inherit' }}>
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatDate('DDDD', date).toUpperCase()}
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: color.bgcolor,
              color: color.text,
              '&:hover': {
                backgroundColor: color.hoverBg
              },
              width: '3rem',
              height: '3rem'
            }}>
            {formatDate('D', date)}
          </Avatar>
        </Stack>
        <Box>
          <Chip
            aria-label="chipHour"
            label={hoursToday === 1 ? `${hoursToday} hr` : `${hoursToday} hrs`}
            variant="outlined"
            size="small"
            sx={{ width: '5em' }}
          />
          <Chip
            aria-label="chipCar"
            label={carsToday === 1 ? `${carsToday} car` : `${carsToday} cars`}
            variant="outlined"
            size="small"
            sx={{ width: '5em' }}
          />
        </Box>
      </Stack>
    )
  }, [])
  interface Props {
    calendar: string
    view: string
    changeCal: any
    changeView: any
  }

  const renderHeader: FC<Props> = useCallback(
    ({ calendar, view, changeCal, changeView }) => {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          mx={1}
          mb={3}
          sx={{ width: '100%' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end">
            <CalendarNav data-testid="calNav" />
            <CalendarPrev data-testid="calPrev" />
            <CalendarToday />
            <CalendarNext date-testid="calNext" />
          </Stack>
          <ToggleButtonGroup
            value={calendar}
            exclusive
            onChange={changeCal}
            sx={customToggleButton}>
            <ToggleButton value="appt">Appointments</ToggleButton>
            <ToggleButton value="resource">Resources</ToggleButton>
          </ToggleButtonGroup>
          <Tabs value={view} onChange={changeView}>
            <Tab value="week" label="week" />
            <Tab value="day" label="day" />
          </Tabs>
        </Stack>
      )
    },
    []
  )

  return { renderResource, renderDayHeader, renderHeader }
}
